import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import type { StockAnalogMatch } from '@/mocks/stockAnalogMock';
import {
  AnalogMatchDetail,
  formatDateShort,
  formatPct,
  pctChipClass,
} from '@/components/AnalogLensAccordion';

type Props = {
  matches: StockAnalogMatch[];
  chartSymbol?: string;
  className?: string;
};

export function MajorStoryDateCards({ matches, chartSymbol, className = '' }: Props) {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const visible = matches.slice(0, 5);
  if (visible.length === 0) return null;

  const handleToggle = (date: string) => {
    setExpandedDate((current) => (current === date ? null : date));
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {visible.map((match, index) => {
        const isOpen = expandedDate === match.date;
        return (
          <motion.div
            key={match.date}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
            className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
          >
            <button
              type="button"
              onClick={() => handleToggle(match.date)}
              className={`flex w-full items-center gap-3 p-6 text-left transition-colors hover:bg-gray-50 ${
                isOpen ? 'bg-gray-50' : ''
              }`}
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-sm font-medium text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {formatDateShort(match.date)}
                  </span>
                  <span className={`text-sm font-medium ${pctChipClass(match.dayReturnPct)}`}>
                    {formatPct(match.dayReturnPct)}
                  </span>
                  <span className="rounded-lg bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">
                    {(match.similarityScore * 100).toFixed(0)}% match
                  </span>
                </div>
                <p className="text-sm leading-relaxed text-gray-700">{match.headline}</p>
              </div>
              <ChevronDown
                className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            <AnimatePresence initial={false}>
              {isOpen ? (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <AnalogMatchDetail
                    match={match}
                    lensId="situational"
                    lensChartSymbol={chartSymbol}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
}
